/**
 * Website-only: restores the campaign-management Redux view from the URL.
 *
 * This is the "read" half of the deep-link wiring (the "write" half lives at the click
 * sites: the tabs, the edit-audience action, the board back button, and the template
 * editor actions). It runs when the pathname / `boardID` / `templateId` change, so
 * entering a wizard sub-view that keeps the same path never re-fires it and resets state.
 *
 * Gated to the website via `isExtensionContextOrWhatsappView()` — the extension / WhatsApp
 * view has no react-router and must keep its Redux-only behavior.
 */
import { bulkAudienceSelectBoardThunk } from "@common-reducers/BoardsThunks";
import { setActiveBulkView, setActiveTab, setCurrentTemplate } from "@common-reducers/CampaignReducer";
import { DBBoardsThunks, DBTemplateThunks, templateSelectOneObjectByQuery } from "@common-reducers/DBServiceThunks";
import { useAppDispatch } from "@common-reducers/hooks/store.hook";
import { useRouter } from "@common-reducers/hooks/use-router";
import { isExtensionContextOrWhatsappView } from "@common/services/detect-context";
import { pathToCampaignView } from "@common/utils/campaign-routes";
import { loadTemplateData, resetScheduledMessageEditor, setTemplateName } from "@common/reducers/ScheduledMessageEditorReducer";
import type { CommonRootState } from "@common/types/common-root-state-type";
import { useStore } from "react-redux";
import { useEffect } from "react";

const firstQueryValue = (v: string | string[] | undefined): string | undefined =>
  Array.isArray(v) ? v[0] : v;

export function useRestoreCampaignViewFromUrl() {
  const dispatch = useAppDispatch();
  // useStore (not useSelector) — we only need the current state at the moment a fetch
  // resolves to look up the loaded template by id. No re-render dependency needed.
  const store = useStore<CommonRootState>();
  const router = useRouter();

  const pathname = router?.pathname ?? "";
  const boardIdFromUrl = firstQueryValue(router?.query?.boardID as any);
  const templateIdFromUrl = firstQueryValue(router?.query?.templateId as any);

  useEffect(() => {
    if (isExtensionContextOrWhatsappView()) return;

    const view = pathToCampaignView(pathname);
    if (!view) return;

    dispatch(setActiveTab(view.activeTab));

    if (view.isAudienceBoard) {
      // Only open the board once we actually have an id; otherwise stay on the audiences tab.
      if (boardIdFromUrl) {
        dispatch(setActiveBulkView(view.activeBulkView));
        dispatch(bulkAudienceSelectBoardThunk({ boardId: boardIdFromUrl }));
        // Cold deep-link: the board may not be in the store yet. The nested TaskView only
        // mounts once it exists, so fetch it here to bootstrap.
        dispatch(DBBoardsThunks.find({ _id: boardIdFromUrl }));
      } else {
        dispatch(setActiveBulkView("campaignManagementView"));
      }
      return;
    }

    if (view.isTemplateEditor) {
      // `?templateId` distinguishes edit-existing from create-new. We always reset the
      // editor first so a previous session's draft doesn't bleed into "new template".
      dispatch(resetScheduledMessageEditor());
      dispatch(setActiveBulkView(view.activeBulkView));

      if (!templateIdFromUrl) {
        // New template: empty defaults.
        dispatch(setCurrentTemplate({
          name: 'New Template',
          message: '',
          asset: undefined,
          imageId: '',
          sendLinkPreview: false,
          linkPreview: {},
          isPoll: false,
          poll: {},
        }));
        // TemplateHeader reads templateName from ScheduledMessageEditorReducer
        // (not CampaignReducer.currentTemplate), so sync it explicitly.
        dispatch(setTemplateName('New Template'));
        return;
      }

      // Edit: try the cached template, otherwise fetch.
      const cached = templateSelectOneObjectByQuery(store.getState().DBTemplateReducer, { _id: templateIdFromUrl });
      if (cached) {
        dispatch(setCurrentTemplate(cached));
        dispatch(loadTemplateData(cached));
      } else {
        dispatch(DBTemplateThunks.find({ _id: templateIdFromUrl })).then(() => {
          const fresh = templateSelectOneObjectByQuery(store.getState().DBTemplateReducer, { _id: templateIdFromUrl });
          if (fresh) {
            dispatch(setCurrentTemplate(fresh));
            dispatch(loadTemplateData(fresh));
          }
        });
      }
      return;
    }

    dispatch(setActiveBulkView(view.activeBulkView));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, boardIdFromUrl, templateIdFromUrl]);
}
